import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tab } from '@hydrodigital/design-system';

import LoadFile from '../../components/LoadFile/Index';
import { getGisFiles } from '../../services/gis';
import { getModelFiles } from '../../services/model';
import HydraulicProfilerService from '../../domain/HydraulicProfiler/HydraulicProfilerService';
import GisTable from './GisTable';
import ModelTable from './ModelTable';
import HistoricalTabla from './HistoricalTable';
import type { PaginationProps } from 'antd';

import './index.scss';
import { useSearchParams } from 'react-router-dom';
import UploadParametersTable from './ParameterTable';
import ProfilerTable from './ProfilerTable';
import { HydraulicProfileData } from '@/domain/HydraulicProfiler/HydraulicProfiler';
const hydraulicProfilerService = new HydraulicProfilerService();
const UploadsView: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [name, setName] = useState<any>(null);
	const [nameProfilerFile, setNameProfilerFile] = useState<any>(null);
	const [typeProfilerFile, setTypeProfilerFile] = useState<any>(null);
	const [fileName, setFileName] = useState<any>(null);
	const [typeGis, setTypeGis] = useState<any>(null);
	const [type, setType] = useState<any>(null);
	const [pagination, setPagination] = useState({
		page: 1,
		pageSize: 10,
		totalRows: 0,
	});
	const [pagination2, setPagination2] = useState({
		page: 1,
		pageSize: 10,
		totalRows: 0,
	});
	const [profilerPagination, setProfilerPagination] = useState({
		page: 1,
		pageSize: 10,
		totalRows: 0,
	});
	const [gisFiles, setGisFiles] = useState<any>([]);
	const [modelFiles, setModelFiles] = useState<any>([]);
	const [historicalFiles, setHistoricalFiles] = useState<any>([]);
	const [profilerFiles, setProfilerFiles] = useState<HydraulicProfileData[]>([]);
	const [selectedTab, setSelectedTab] = useState('GIS');
	const [selectedLocation, setSelectedLocation] = useState(null);

	const handleUpdateGisFiles = useCallback(
		(loading = true) => {
			const userId = localStorage.getItem('userId');
			getGisFiles(
				userId,
				{
					page: pagination.page,
					size: pagination.pageSize,
					fileName,
					typeGis,
				},
				loading,
			)
				.then((response) => {
					setPagination((prev) => {
						return {
							...prev,
							totalRows: response.totalRecords,
						};
					});
					setGisFiles(response.filesUploaded);
				})
				.catch((error) => {
					console.error(error);
				});
		},
		[pagination.page, pagination.pageSize, fileName, typeGis],
	);

	const handleUpdateModelFiles = useCallback(async (loading = true) => {
		let organizationId = [];
		const printItems = localStorage.getItem('security');
		if (printItems) {
			organizationId = JSON.parse(printItems).organizationId;
		}
		return getModelFiles(
			organizationId,
			{
				page: pagination2.page,
				size: pagination2.pageSize,
				name,
				type,
				locationId: selectedLocation,
			},
			loading,
		)
			.then((response) => {
				setPagination2((prev) => ({
					...prev,
					totalRows: response.totalRecords,
				}));
				setModelFiles(response.getAllResponseDTOList);
			})
			.catch((error) => {
				console.error(error);
			});
	}, [pagination2.page, pagination2.pageSize, name, type, selectedLocation]);

	const handleUpdateProfilerFiles = useCallback(async (loading = true) => {
		let organizationId = [];
		const printItems = localStorage.getItem('security');
		if (printItems) {
			organizationId = JSON.parse(printItems).organizationId;
		}
		hydraulicProfilerService
			.getAllProfilesPagination(
				profilerPagination.page,
				profilerPagination.pageSize,
				nameProfilerFile,
				selectedLocation || 0,
			)
			.then((response) => {
				setProfilerPagination((prev) => ({
					...prev,
					totalRows: response.totalRecords,
				}));
				setProfilerFiles(response.data);
			})
			.catch((error) => {
				console.error(error);
			});
	}, [profilerPagination.page, profilerPagination.pageSize, nameProfilerFile, selectedLocation]);

	useEffect(() => {
		handleUpdateProfilerFiles();
	}, [handleUpdateProfilerFiles]);

	const onChangePagination = (page: number, pageSize?: number) => {
		setPagination({ ...pagination, page, pageSize: pageSize || pagination.pageSize });
	};

	const onChangePagination2 = (page: number, pageSize?: number) => {
		setPagination2({ ...pagination2, page, pageSize: pageSize || pagination2.pageSize });
	};

	const onChangeProfilerPagination = (page: number, pageSize?: number) => {
		setProfilerPagination({ ...profilerPagination, page, pageSize: pageSize || profilerPagination.pageSize });
	};

	useEffect(() => {
		setSelectedTab(searchParams.get('selectedTab') || 'GIS');
	}, [searchParams]);

	
	const gisInitialized = useRef(false);
	
	const prevGisValues = useRef({ page: pagination.page, pageSize: pagination.pageSize, fileName, typeGis });

	
	useEffect(() => {
		if (selectedTab !== 'GIS') {
			gisInitialized.current = false;
			return;
		}

		const prev = prevGisValues.current;
		const filtersChanged = prev.fileName !== fileName || prev.typeGis !== typeGis;
		const paginationChanged = prev.page !== pagination.page || prev.pageSize !== pagination.pageSize;

		
		prevGisValues.current = { page: pagination.page, pageSize: pagination.pageSize, fileName, typeGis };

		
		
		if (filtersChanged && gisInitialized.current) {
			if (pagination.page !== 1) {
				setPagination((p) => ({ ...p, page: 1 }));
				return; 
			}
		}

		
		if (!gisInitialized.current || paginationChanged || filtersChanged) {
			gisInitialized.current = true;
			handleUpdateGisFiles();
		}
	}, [selectedTab, pagination.page, pagination.pageSize, fileName, typeGis, handleUpdateGisFiles]);

	
	const modelInitialized = useRef(false);
	
	const prevModelValues = useRef({ page: pagination2.page, pageSize: pagination2.pageSize, name, type, selectedLocation });


	useEffect(() => {
		if (selectedTab !== 'MODEL') {
			modelInitialized.current = false;
			return;
		}

		const prev = prevModelValues.current;
		const filtersChanged = prev.name !== name || prev.type !== type || prev.selectedLocation !== selectedLocation;
		const paginationChanged = prev.page !== pagination2.page || prev.pageSize !== pagination2.pageSize;


		prevModelValues.current = { page: pagination2.page, pageSize: pagination2.pageSize, name, type, selectedLocation };

		if (filtersChanged && modelInitialized.current) {
			if (pagination2.page !== 1) {
				setPagination2((p) => ({ ...p, page: 1 }));
				return;
			}
		}

		if (!modelInitialized.current || paginationChanged || filtersChanged) {
			modelInitialized.current = true;
			handleUpdateModelFiles();
		}
	}, [selectedTab, pagination2.page, pagination2.pageSize, name, type, selectedLocation, handleUpdateModelFiles]);

	useEffect(() => {
		setProfilerPagination((prev) => ({ ...prev, page: 1 }));
	}, [nameProfilerFile, typeProfilerFile]);

	const tabs = [
		{
			id: 'GIS',
			label: 'GIS FILES',
			content:
				gisFiles.length <= 0 && !typeGis && !fileName ? (
					<LoadFile url={'gisfiles'} text="Upload GIS Files" />
				) : (
					<GisTable
						handleUpdateGisFiles={handleUpdateGisFiles}
						files={gisFiles}
						onChangePagination={onChangePagination}
						pagination={pagination}
						setInputSearch={setFileName}
						setTypeGis={setTypeGis}
					/>
				),
		},
		{
			id: 'MODEL',
			label: 'MODEL FILES',
			content:
				modelFiles.length > 0 || type || name || selectedLocation ? (
					<ModelTable
						handleUpdateModelFiles={handleUpdateModelFiles}
						files={modelFiles}
						onChangePagination={onChangePagination2}
						pagination={pagination2}
						setInputSearch={setName}
						setType={setType}
						setModelFiles={setModelFiles}
						setSelectedLocation={setSelectedLocation}
						selectedLocation={selectedLocation}
					/>
				) : (
					<LoadFile url={'modelfiles'} text={'Upload Models Files'} />
				),
		},
		{
			id: 'HISTORICAL',
			label: 'HISTORICAL FILES',
			content: <HistoricalTabla isCurrentTab={selectedTab == 'HISTORICAL'} />,
		},
		{
			id: 'PARAMETER',
			label: 'PARAMETER FILES',
			content: <UploadParametersTable isCurrentTab={selectedTab == 'PARAMETER'} />,
		},
		{
			id: 'PROFILER',
			label: 'NETWORK PROFILE',
			content:
				profilerFiles.length > 0 || nameProfilerFile || typeProfilerFile || selectedLocation ? (
					<ProfilerTable
						handleUpdateModelFiles={handleUpdateProfilerFiles}
						files={profilerFiles}
						onChangePagination={onChangeProfilerPagination}
						pagination={profilerPagination}
						setInputSearch={setNameProfilerFile}
						setType={setTypeProfilerFile}
						setSelectedLocation={setSelectedLocation}
						selectedLocation={selectedLocation}
						handleUpdateProfilerFiles={handleUpdateProfilerFiles}
					/>
				) : (
					<LoadFile url={'profilerfiles'} text={'Upload Profiler Files'} />
				),
		},
	];

	return (
		<div>
			<div className="text-title-page">Upload Files</div>
			<Tab
				tabs={tabs}
				showContent
				orientation="horizontal"
				activeTab={selectedTab}
				onTabChange={(id) => {
					setSelectedTab(id);
					setSearchParams({ selectedTab: id });
				}}
			/>
		</div>
	);
};

export default UploadsView;
